import {useMemo} from "react";
import {IntlProvider} from "react-intl";
import {useSelector} from "react-redux";
import * as Sentry from "@sentry/react";

import {ContentWrapper, Footer, Header} from "webform/components";
import {FormWrapper} from "webform/views/Form";
import {FullscreenErrorHandler} from "webform/views/FullScreenErrorHandler";
import {Error} from "webform/views/Error";
import lookupTranslations from "i18n/LookupTranslations";
import {type RootState} from "webform/store/store";
import {globalErrorSelector} from "webform/store/selectors/errorSelector";
import {submitFormIsSubmittedSelector} from "webform/store/selectors/submitFormSelector";
import {isWebformAvailableSelector, isWebformAvailableLoadingSelector} from "webform/store/selectors/configSelectors";
import {useBrandedResources} from "hooks/useBrandedResources";
import {GlobalStyle, MainContainer} from "./app.styled";

function App() {
    const locale = useSelector((state: RootState) => state.config.locale);
    const messages = useMemo(() => lookupTranslations(locale), [locale]);
    const errors = useSelector(globalErrorSelector);
    const isFormSubmitted = useSelector(submitFormIsSubmittedSelector);
    const isWebformAvailable = useSelector(isWebformAvailableSelector);
    const isWebformAvailableLoading = useSelector(isWebformAvailableLoadingSelector);
    const isFullHeight = errors.length > 0 || isFormSubmitted || (!isWebformAvailable && !isWebformAvailableLoading);

    useBrandedResources();

    return (
        <IntlProvider locale={locale} defaultLocale="en-gb" messages={messages}>
            <Sentry.ErrorBoundary fallback={<Error />}>
                <MainContainer>
                    <GlobalStyle />
                    <Header />
                    <ContentWrapper fullHeight={isFullHeight}>
                        <FullscreenErrorHandler>
                            <FormWrapper />
                        </FullscreenErrorHandler>
                    </ContentWrapper>
                    <Footer />
                </MainContainer>
            </Sentry.ErrorBoundary>
        </IntlProvider>
    );
}

export default App;
